import { FC, Fragment, memo, useMemo } from 'react'
import { BreadcrumbList } from '../../models/common/breadcrumb-model'
import { Offer } from '../../models/common/offers-model'
import { SuitablesModel } from '../../models/common/suitables-model'
import { useDateFormatter } from '../../hooks/date'
import { Container, InnerContainer, HeadingSection, Title, Subtitle, SeoHref, StickyContainer } from './styles'
import HeaderSection from '../common/header-section'
import { useTranslation } from '../../hooks/locale'
import FooterSection from '../common/footer'
import NewsLetter from '../common/news-letter-section'
import Pagination from '../common/pagination'
import NavigationSectionTransport from '../../transports/common/navigation-section-transport'
import FooterSectionTransport from '../../transports/common/footer-section-transport'
import NewsletterSectionTransport from '../../transports/common/news-letter-section-transport'
import ButtonTransport from '../../transports/common/button-transport'
import { ListingSectionObject } from '../../transports/common/listing-section-transport'
import ListingItem from './listing-item'
import { useRouting } from '../../hooks/routes'
import { useUserDetails } from '../../hooks/users'
import { BannerSlot } from '../../transports/home-page-transport'
import BannerSection from '../home/banner-section'

interface Props {
    objectOverviewLink: string
    navigationSection: NavigationSectionTransport
    footerSection: FooterSectionTransport
    newsLetterSection: NewsletterSectionTransport
    banner?: BannerSlot | null
    footerLogo: ButtonTransport
    socialMediaLinks: ButtonTransport[]
    objects: ListingSectionObject[]
    heading?: string
    subheading?: string
    ownerName?: string | null
    totalCount?: number
    currentPage?: number
    totalPages?: number
}

const ObjectOverview: FC<Props> = ({
    objectOverviewLink,
    navigationSection,
    footerSection,
    newsLetterSection,
    banner,
    footerLogo,
    socialMediaLinks,
    objects,
    heading,
    subheading,
    ownerName,
    totalCount,
    currentPage,
    totalPages,
}) => {
    const { t } = useTranslation()
    const { r } = useRouting()
    const { format } = useDateFormatter()
    const breadcrumbList = useMemo(() => new BreadcrumbList(t), [t])
    const offerModel = useMemo(() => new Offer(t, format), [t, format])
    const suitablesModel = useMemo(() => new SuitablesModel(t), [t])

    const { userDetails } = useUserDetails({ additionalDetails: true })
    const firstName = userDetails?.firstName
    const isLoggedIn = !!userDetails?.id

    const routePattern = r('objectOverview')

    const pagePrefix = t('objectOverview.pagePrefix')

    const buildUrl = (targetPage: number) => {
        let newPath = routePattern.replace('{objectOverviewLink}', objectOverviewLink)

        if (targetPage > 1) {
            const slug = `${pagePrefix}${targetPage}`
            newPath = newPath.replace('{page?}', slug)
        } else {
            newPath = newPath.replace(/\/?{page\?}/, '')
        }
        return newPath
    }

    const _handlePageChange = (page: number) => {
        const newPath = buildUrl(page)
        globalThis.location.href = newPath
    }

    const url = buildUrl(1)

    const effectiveTotalCount = totalCount ?? objects?.length ?? 0
    const firstSeoRegion = objects?.[0]?.seoRegion
    const regionName = firstSeoRegion?.name || ''
    const regionUrl = firstSeoRegion?.url?.url
    const regionNoCrawl = firstSeoRegion?.url?.noCrawl

    const resolvedTitle =
        heading ?? (ownerName ? t('objectOverview.titleWithOwner', { name: ownerName }) : t('objectOverview.title'))

    const subtitleText =
        subheading ??
        (ownerName
            ? t('objectOverview.subtitleWithOwner', { name: ownerName, count: effectiveTotalCount })
            : t('objectOverview.subtitle', { count: effectiveTotalCount }))

    return (
        <Fragment>
            <StickyContainer>
                <HeaderSection
                    firstName={firstName}
                    isLoggedIn={isLoggedIn}
                    url={url}
                    headerSection={{ navigationSectionResponse: navigationSection }}
                    showVacationIdeasNavButton
                />
            </StickyContainer>

            <main id="object-overview-main">
                <Container>
                    {!!banner && <BannerSection banner={banner} />}
                    <InnerContainer>
                        <HeadingSection>
                            <Title>{resolvedTitle}</Title>
                            <Subtitle>
                                {subtitleText}{' '}
                                <SeoHref href={regionUrl} noCrawl={regionNoCrawl}>
                                    {regionName}
                                </SeoHref>
                                .
                            </Subtitle>
                        </HeadingSection>

                        {objects.map((item, index) => (
                            <ListingItem
                                key={item.id}
                                listingObject={item}
                                index={index}
                                requestUrl={url}
                                breadcrumbModel={breadcrumbList}
                                offerModel={offerModel}
                                suitablesModel={suitablesModel}
                            />
                        ))}

                        <Pagination
                            currentPage={currentPage ?? 1}
                            totalPages={totalPages ?? 1}
                            onPageChange={_handlePageChange}
                        />
                    </InnerContainer>
                    <NewsLetter newsletter={newsLetterSection} url={url} />
                </Container>
            </main>
            <FooterSection footerSection={footerSection} footerLogo={footerLogo} socialMediaLinks={socialMediaLinks} />
        </Fragment>
    )
}

export default memo(ObjectOverview)
